import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { translateDomain } from '../../utils/labels_utils'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  Spinner,
} from 'Components/common'
import {
  getAssessmentByToken,
  updateVendorResponse,
  uploadEvidence,
  submitVendorAssessment
} from 'services/vendor-assessment-service'
import { getPublicBranding } from 'services/company-service'
import { isVoTenant } from 'utils/tenant_utils'
import { applyPrimaryColorVars, clearPrimaryColorVars } from 'utils/color_utils'
import { Dialog, TextField } from 'Components/ui'
import {
  GppGoodIcon,
  GppBadIcon,
  GppMaybeIcon,
  CloudUploadIcon,
  SendIcon,
  LockIcon,
  RefreshIcon,
  CheckCircleIcon,
} from './AssessmentIcons'
import {
  PageWrapper,
  ExternalHeader,
  HeaderLeft,
  HeaderLogo,
  HeaderLogoText,
  HeaderDivider,
  HeaderCompanyInfo,
  HeaderRequestedLabel,
  HeaderCompanyName,
  HeaderRight,
  HeaderDot,
  HeaderLinkStatus,
  PageContainer,
  LoadingWrapper,
  ErrorContainer,
  ErrorTitle,
  SubmittedCard,
  SubmittedTitle,
  RemediationNoteAlert,
  RemediationNoteTitle,
  RemediationNoteText,
  ErrorText,
  Section,
  InfoCard,
  HeaderTitle,
  HeaderMeta,
  ProgressSection,
  ProgressLabelRow,
  ProgressLabel,
  ProgressValue,
  ProgressTrack,
  ProgressFill,
  InstructionAlert,
  InstructionText,
  AccordionCard,
  AccordionHeader,
  AccordionHeaderLeft,
  DomainName,
  DomainProgressBadge,
  ChevronWrapper,
  AccordionBody,
  ControlWrapper,
  ControlHeader,
  ControlTitle,
  LockedBadge,
  MaturityRadioGroup,
  RadioLabel,
  RadioInput,
  LevelLabel,
  LevelDesc,
  EvidenceSection,
  EvidenceLabel,
  StyledUploadButton,
  RefreshButton,
  FileList,
  FileItem,
  FileIconWrapper,
  FileDetails,
  FileName,
  FileMeta,
  ScanChip,
  ActionRow,
  SavingIndicator,
  SubmitButton,
} from './VendorAssessmentForm.styles'

const str = (val) => {
  if (!val) return ''
  if (typeof val === 'object') {
    const lang = (i18n.language || 'en').split('-')[0]
    const raw = val[lang] || val.en || val.pt || val.es || Object.values(val)[0] || ''
    return typeof raw === 'string' ? raw : String(raw)
  }
  return String(val)
}

const getScanStatusDisplay = (scanStatus, t) => {
  if (!scanStatus) {
    return {
      icon: <GppGoodIcon width="16" height="16" />,
      color: 'var(--vo-success)',
      label: t('gap-analysis:assessment.vendorForm.scanStatus.verified'),
      chipColor: 'success'
    }
  }
  switch (scanStatus) {
    case 'clean':
      return {
        icon: <GppGoodIcon width="16" height="16" />,
        color: 'var(--vo-success)',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.scanPassed'),
        chipColor: 'success'
      }
    case 'malicious':
      return {
        icon: <GppBadIcon width="16" height="16" />,
        color: 'var(--vo-danger)',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.threatDetected'),
        chipColor: 'error'
      }
    case 'error':
      return {
        icon: <GppMaybeIcon width="16" height="16" />,
        color: 'var(--vo-warning)',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.scanError'),
        chipColor: 'warning'
      }
    case 'pending':
    default:
      return {
        icon: <GppMaybeIcon width="16" height="16" />,
        color: 'var(--vo-slate)',
        label: t('gap-analysis:assessment.vendorForm.scanStatus.scanning'),
        chipColor: 'default'
      }
  }
}

const InfoCircleSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
)

const ChevronSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
)

export const VendorAssessmentForm = () => {
  const { t } = useTranslation(['gap-analysis', 'common'])
  const { token } = useParams()

  const maturityLevels = [
    { value: 0, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l0.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l0.description') },
    { value: 1, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l1.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l1.description') },
    { value: 2, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l2.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l2.description') },
    { value: 3, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l3.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l3.description') },
    { value: 4, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l4.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l4.description') },
    { value: 5, label: t('gap-analysis:assessment.vendorForm.maturityLevels.l5.label'), description: t('gap-analysis:assessment.vendorForm.maturityLevels.l5.description') },
  ]
  const queryClient = useQueryClient()

  const [expandedDomainIndex, setExpandedDomainIndex] = useState(null)
  const [localNotes, setLocalNotes] = useState({})
  const debounceTimers = useRef({})
  const accordionRefs = useRef({})

  const toggleDomain = useCallback((domainIdx) => {
    setExpandedDomainIndex(prev => {
      const opening = prev !== domainIdx
      if (opening) {
        requestAnimationFrame(() => {
          const el = accordionRefs.current[domainIdx]
          if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 88
            window.scrollTo({ top, behavior: 'smooth' })
          }
        })
      }
      return opening ? domainIdx : null
    })
  }, [])

  // Set once this session's submit succeeds; ALREADY_SUBMITTED from the server covers a reload.
  const [justSubmitted, setJustSubmitted] = useState(false)

  const { data: assessmentData, isLoading: assessmentLoading, error: assessmentError } = useQuery(
    ['vendor-assessment', token],
    () => getAssessmentByToken(token),
    {
      enabled: !!token && !justSubmitted,
      retry: false,
      onError: (error) => {
        // An already-submitted assessment is not an error for the vendor — the page says so.
        if (error?.response?.data?.code === 'ALREADY_SUBMITTED') return
        Dialog.alert({
          icon: 'error',
          title: t('gap-analysis:assessment.vendorForm.swal.invalidTitle'),
          message: t('gap-analysis:assessment.vendorForm.swal.invalidText')
        })
      }
    }
  )
  const alreadySubmitted = justSubmitted || assessmentError?.response?.data?.code === 'ALREADY_SUBMITTED'

  // Translated message for a rejected vendor action (server codes from vendor-submission.controller).
  const vendorErrorMessage = (error, fallbackKey) => {
    const code = error?.code || error?.response?.data?.code
    if (code === 'SUBMIT_EVIDENCE_MISSING') {
      return t('gap-analysis:assessment.vendorForm.errors.SUBMIT_EVIDENCE_MISSING', { count: error?.details?.missingCount ?? 0 })
    }
    return code && i18n.exists(`gap-analysis:assessment.vendorForm.errors.${code}`)
      ? t(`gap-analysis:assessment.vendorForm.errors.${code}`)
      : t(fallbackKey)
  }

  const assessment = assessmentData?.data?.data?.assessment
  const controls = assessmentData?.data?.data?.controlResponses || []
  // On a reload after submitting there is no assessment payload, only the ALREADY_SUBMITTED body,
  // which carries the requesting tenant so that page keeps the same branded header.
  const submittedBody = assessmentError?.response?.data?.code === 'ALREADY_SUBMITTED' ? assessmentError.response.data : null
  const companyKey = assessment?.companyKey || submittedBody?.companyKey
  const companyName = assessment?.companyName || submittedBody?.companyName

  const { data: brandingData, isLoading: brandingLoading } = useQuery(
    ['publicBranding', companyKey],
    () => getPublicBranding(companyKey),
    { enabled: !!companyKey, retry: false, staleTime: 5 * 60 * 1000 }
  )

  // VO's own tenant always shows its real logo here, never a requesting vendor's custom
  // branding (VO isn't gated by plan eligibility like a customer tenant would be) — this check
  // runs before brandingData is even considered. Any other tenant never gets the VO logo:
  // logoUrl → logoDarkUrl → the company name as a text wordmark. Each image candidate that
  // fails to load (onError) falls through to the next one.
  const brandingInfo = brandingData?.data && !brandingData.data.default ? brandingData.data : null
  const logoCandidates = React.useMemo(
    () => isVoTenant(companyKey)
      ? ['/img/logo-horizontal.png']
      : [brandingInfo?.logoUrl, brandingInfo?.logoDarkUrl].filter(Boolean),
    [companyKey, brandingInfo?.logoUrl, brandingInfo?.logoDarkUrl]
  )
  const [failedLogoCount, setFailedLogoCount] = useState(0)
  useEffect(() => { setFailedLogoCount(0) }, [logoCandidates])
  const headerLogoSrc = logoCandidates[failedLogoCount] || null

  useEffect(() => {
    if (!assessment?.companyName) return undefined
    const previousTitle = document.title
    document.title = `${assessment.companyName} – ${t('gap-analysis:assessment.vendorForm.title')}`
    return () => { document.title = previousTitle }
  }, [assessment?.companyName, t])

  useEffect(() => {
    const b = brandingData?.data
    if (!b || b.default) return
    const root = document.documentElement
    if (b.primaryColor) applyPrimaryColorVars(root, b.primaryColor)
    if (b.secondaryColor) root.style.setProperty('--color-secondary', b.secondaryColor)
    if (b.faviconUrl) {
      const favicon = document.querySelector('link[rel="icon"]')
      if (favicon) favicon.href = b.faviconUrl
    }
    return () => {
      clearPrimaryColorVars(root)
      root.style.removeProperty('--color-secondary')
    }
  }, [brandingData])

  const controlsByDomain = React.useMemo(() => {
    const grouped = {}
    controls.forEach(control => {
      const domainKey = str(control.domain)
      if (!grouped[domainKey]) grouped[domainKey] = []
      grouped[domainKey].push(control)
    })
    return grouped
  }, [controls])

  const progress = React.useMemo(() => {
    if (controls.length === 0) return 0
    const answered = controls.filter(c => c.response?.maturityLevel !== undefined).length
    return Math.round((answered / controls.length) * 100)
  }, [controls])

  useEffect(() => {
    if (controls.length > 0) {
      setLocalNotes(prev => {
        const next = { ...prev }
        controls.forEach(control => {
          if (control.response?._id && !(control.response._id in next)) {
            next[control.response._id] = control.response?.notes || ''
          }
        })
        return next
      })
    }
  }, [controls])

  useEffect(() => {
    const timers = debounceTimers.current
    return () => { Object.values(timers).forEach(clearTimeout) }
  }, [])

  const updateResponseMutation = useMutation(
    ({ controlResponseId, maturityLevel, notes }) =>
      updateVendorResponse(token, controlResponseId, { maturityLevel, notes }),
    {
      onSuccess: () => { queryClient.invalidateQueries(['vendor-assessment', token]) },
      onError: () => {
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.vendorForm.swal.errorTitle'), message: t('gap-analysis:assessment.vendorForm.swal.errorSaveResponse') })
      }
    }
  )

  const updateNotesMutation = useMutation(
    ({ controlResponseId, notes }) => updateVendorResponse(token, controlResponseId, { notes }),
    {
      onError: () => {
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.vendorForm.swal.errorTitle'), message: t('gap-analysis:assessment.vendorForm.swal.errorSaveNotes') })
      }
    }
  )

  const uploadMutation = useMutation(
    ({ controlResponseId, formData }) => uploadEvidence(token, controlResponseId, formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendor-assessment', token])
        Dialog.fire({ icon: 'info', title: t('gap-analysis:assessment.vendorForm.swal.uploadedTitle'), text: t('gap-analysis:assessment.vendorForm.swal.uploadedText'), timer: 2000, showConfirmButton: false })
      },
      onError: (error) => {
        queryClient.invalidateQueries(['vendor-assessment', token])
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.vendorForm.swal.uploadFailedTitle'), message: vendorErrorMessage(error, 'gap-analysis:assessment.vendorForm.errors.EVIDENCE_UPLOAD_FAILED') })
      }
    }
  )

  const submitMutation = useMutation(
    () => submitVendorAssessment(token),
    {
      onSuccess: () => {
        setJustSubmitted(true)
        Dialog.alert({ icon: 'success', title: t('gap-analysis:assessment.vendorForm.swal.submittedTitle'), message: t('gap-analysis:assessment.vendorForm.swal.submittedHtml'), confirmLabel: t('common:buttons.close') })
      },
      onError: (error) => {
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.vendorForm.swal.submitFailedTitle'), message: vendorErrorMessage(error, 'gap-analysis:assessment.vendorForm.swal.submitFailedText') })
      }
    }
  )

  const handleMaturityChange = (controlResponseId, maturityLevel) => {
    updateResponseMutation.mutate({ controlResponseId, maturityLevel })
  }

  const handleNotesChange = useCallback((controlResponseId, notes) => {
    setLocalNotes(prev => ({ ...prev, [controlResponseId]: notes }))
    if (debounceTimers.current[controlResponseId]) clearTimeout(debounceTimers.current[controlResponseId])
    debounceTimers.current[controlResponseId] = setTimeout(() => {
      updateNotesMutation.mutate({ controlResponseId, notes })
    }, 800)
  }, [updateNotesMutation])

  const handleFileUpload = (controlResponseId, event) => {
    const file = event.target.files[0]
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    uploadMutation.mutate({ controlResponseId, formData })
  }

  const handleSubmit = async () => {
    const confirmed = await Dialog.confirm({
      title: t('gap-analysis:assessment.vendorForm.swal.submitTitle'),
      message: t('gap-analysis:assessment.vendorForm.swal.submitHtml', { progress }),
      confirmLabel: t('gap-analysis:assessment.vendorForm.swal.submitButton'),
    })
    if (confirmed) submitMutation.mutate()
  }

  if (assessmentLoading) {
    return (
      <PageWrapper>
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      </PageWrapper>
    )
  }

  const renderHeader = (showLinkStatus) => (
    <ExternalHeader>
      <HeaderLeft>
        {headerLogoSrc ? (
          <HeaderLogo
            key={headerLogoSrc}
            src={headerLogoSrc}
            alt={str(companyName)}
            onError={() => setFailedLogoCount(n => n + 1)}
          />
        ) : (
          !(companyKey && brandingLoading) && <HeaderLogoText>{str(companyName)}</HeaderLogoText>
        )}
        <HeaderDivider />
        <HeaderCompanyInfo>
          <HeaderRequestedLabel>{t('gap-analysis:assessment.vendorForm.header.requestedByLabel')}</HeaderRequestedLabel>
          <HeaderCompanyName>{str(companyName)}</HeaderCompanyName>
        </HeaderCompanyInfo>
      </HeaderLeft>
      {showLinkStatus && (
        <HeaderRight>
          <HeaderDot />
          <HeaderLinkStatus>{t('gap-analysis:assessment.vendorForm.header.linkActive')}</HeaderLinkStatus>
        </HeaderRight>
      )}
    </ExternalHeader>
  )

  if (alreadySubmitted) {
    return (
      <PageWrapper>
        {companyKey && renderHeader(false)}
        <PageContainer>
          <SubmittedCard data-testid="vendor-already-submitted">
            <CheckCircleIcon aria-hidden="true" />
            <SubmittedTitle>{t('gap-analysis:assessment.vendorForm.alreadySubmittedTitle')}</SubmittedTitle>
            <ErrorText>{t('gap-analysis:assessment.vendorForm.alreadySubmittedText')}</ErrorText>
          </SubmittedCard>
        </PageContainer>
      </PageWrapper>
    )
  }

  if (!assessment) {
    return (
      <PageWrapper>
        <ErrorContainer>
          <ErrorTitle>{t('gap-analysis:assessment.vendorForm.invalidLink')}</ErrorTitle>
          <ErrorText>{t('gap-analysis:assessment.vendorForm.invalidLinkText')}</ErrorText>
        </ErrorContainer>
      </PageWrapper>
    )
  }

  const domainEntries = Object.entries(controlsByDomain)

  return (
    <PageWrapper>
      {renderHeader(true)}
    <PageContainer>
      <Section>
        <InfoCard>
          <HeaderTitle>{t('gap-analysis:assessment.vendorForm.title')}</HeaderTitle>
          <HeaderMeta>
            {t('gap-analysis:assessment.vendorForm.requestedBy')} <strong>{str(assessment.companyName)}</strong>
            {' · '}
            {t('gap-analysis:assessment.vendorForm.framework')} <strong>{str(assessment.frameworkName)}</strong>
          </HeaderMeta>
          <ProgressSection>
            <ProgressLabelRow>
              <ProgressLabel>{t('gap-analysis:assessment.vendorForm.progress')}</ProgressLabel>
              <ProgressValue>{progress}%</ProgressValue>
            </ProgressLabelRow>
            <ProgressTrack>
              <ProgressFill $progress={progress} />
            </ProgressTrack>
          </ProgressSection>
        </InfoCard>

        <InstructionAlert>
          <InfoCircleSVG />
          <InstructionText>{t('gap-analysis:assessment.vendorForm.instructions')}</InstructionText>
        </InstructionAlert>

        {assessment.remediationNotes && controls.some(c => c.response && !c.response.locked) && (
          <RemediationNoteAlert data-testid="vendor-remediation-note">
            <RemediationNoteTitle>{t('gap-analysis:assessment.vendorForm.remediationNoteTitle')}</RemediationNoteTitle>
            <RemediationNoteText>{assessment.remediationNotes}</RemediationNoteText>
          </RemediationNoteAlert>
        )}

        {domainEntries.map(([domain, domainControls], domainIdx) => {
          const isOpen = expandedDomainIndex === domainIdx
          const domainAnswered = domainControls.filter(c => c.response?.maturityLevel !== undefined).length

          return (
            <AccordionCard key={domain} ref={el => { accordionRefs.current[domainIdx] = el }}>
              <AccordionHeader
                onClick={() => toggleDomain(domainIdx)}
              >
                <AccordionHeaderLeft>
                  <DomainName>{translateDomain(t, domain)}</DomainName>
                  <DomainProgressBadge>
                    {domainAnswered}/{domainControls.length} {t('gap-analysis:assessment.vendorForm.answered')}
                  </DomainProgressBadge>
                </AccordionHeaderLeft>
                <ChevronWrapper $open={isOpen}>
                  <ChevronSVG />
                </ChevronWrapper>
              </AccordionHeader>

              {isOpen && (
                <AccordionBody>
                  {domainControls.map((control, idx) => {
                    const isLast = idx === domainControls.length - 1
                    const isDisabled = !!control.response?.locked

                    return (
                      <ControlWrapper key={control._id} $last={isLast}>
                        <ControlHeader>
                          <ControlTitle>
                            {idx + 1}. {str(control.description)}
                          </ControlTitle>
                          {isDisabled && (
                            <LockedBadge>
                              <LockIcon width={12} height={12} />
                              {t('gap-analysis:assessment.vendorForm.locked')}
                            </LockedBadge>
                          )}
                        </ControlHeader>

                        <MaturityRadioGroup>
                          {maturityLevels.map(level => {
                            const isSelected = control.response?.maturityLevel === level.value
                            return (
                              <RadioLabel
                                key={level.value}
                                $selected={isSelected}
                                $disabled={isDisabled}
                                htmlFor={`${control._id}-${level.value}`}
                              >
                                <RadioInput
                                  type="radio"
                                  id={`${control._id}-${level.value}`}
                                  name={`maturity-${control._id}`}
                                  value={level.value}
                                  checked={isSelected}
                                  disabled={isDisabled}
                                  onChange={() => handleMaturityChange(control.response._id, level.value)}
                                />
                                <div>
                                  <LevelLabel>{level.label}</LevelLabel>
                                  <LevelDesc>{level.description}</LevelDesc>
                                </div>
                              </RadioLabel>
                            )
                          })}
                        </MaturityRadioGroup>

                        <TextField
                          multiline
                          fullWidth
                          rows={2}
                          placeholder={t('gap-analysis:assessment.vendorForm.additionalNotes')}
                          value={localNotes[control.response?._id] ?? control.response?.notes ?? ''}
                          onChange={(e) => handleNotesChange(control.response._id, e.target.value)}
                          disabled={isDisabled}
                        />

                        {/* Every assessed control can take evidence: mandatory for Tier 1
                            (evidenceRequired), optional for Tier 2 — previously the upload only
                            rendered when required, so Tier 2 vendors could never attach any. */}
                        <EvidenceSection>
                          <EvidenceLabel data-testid="evidence-label">
                            {control.evidenceRequired
                              ? t('gap-analysis:assessment.vendorForm.evidenceRequired')
                              : t('gap-analysis:assessment.vendorForm.evidenceOptional')}
                          </EvidenceLabel>
                          <StyledUploadButton
                            type="button"
                            disabled={uploadMutation.isLoading || isDisabled}
                          >
                            <CloudUploadIcon width={16} height={16} />
                            {t('gap-analysis:assessment.vendorForm.uploadEvidence')}
                            <input
                              type="file"
                              hidden
                              accept=".pdf,.png,.jpg,.jpeg"
                              onChange={(e) => handleFileUpload(control.response._id, e)}
                            />
                          </StyledUploadButton>

                          {control.response?.evidenceFiles?.some(f => f.scanStatus === 'pending') && (
                            <RefreshButton
                              type="button"
                              onClick={() => queryClient.invalidateQueries(['vendor-assessment', token])}
                            >
                              <RefreshIcon width={14} height={14} />
                              {t('gap-analysis:assessment.vendorForm.checkScanStatus')}
                            </RefreshButton>
                          )}

                          {control.response?.evidenceFiles?.length > 0 && (
                            <FileList>
                              {control.response.evidenceFiles.map((file) => {
                                const scanDisplay = getScanStatusDisplay(file.scanStatus, t)
                                return (
                                  <FileItem key={file._id}>
                                    <FileIconWrapper $color={scanDisplay.color} title={scanDisplay.label}>
                                      {scanDisplay.icon}
                                    </FileIconWrapper>
                                    <FileDetails>
                                      <FileName>{file.fileName}</FileName>
                                      <FileMeta $malicious={file.scanStatus === 'malicious'}>
                                        {file.scanStatus === 'malicious'
                                          ? `${t('gap-analysis:assessment.vendorForm.scanStatus.threatDetected')}${file.scanResultDetail ? ': ' + file.scanResultDetail : ''}`
                                          : file.scanStatus === 'pending'
                                            ? t('gap-analysis:assessment.vendorForm.scanStatus.scanning')
                                            : `${(file.fileSize / 1024).toFixed(1)} KB`
                                        }
                                      </FileMeta>
                                    </FileDetails>
                                    <ScanChip $variant={scanDisplay.chipColor}>{scanDisplay.label}</ScanChip>
                                  </FileItem>
                                )
                              })}
                            </FileList>
                          )}
                        </EvidenceSection>
                      </ControlWrapper>
                    )
                  })}
                </AccordionBody>
              )}
            </AccordionCard>
          )
        })}

        <ActionRow>
          {(updateResponseMutation.isLoading || updateNotesMutation.isLoading) && (
            <SavingIndicator>{t('gap-analysis:assessment.vendorForm.saving')}</SavingIndicator>
          )}
          <SubmitButton
            type="button"
            onClick={handleSubmit}
            disabled={submitMutation.isLoading || progress < 100}
          >
            {submitMutation.isLoading ? (
              <Spinner $size={20} $borderColor="color-mix(in srgb, var(--vo-white) 60%, transparent)" />
            ) : (
              <><SendIcon width={18} height={18} /> {t('gap-analysis:assessment.vendorForm.submitButton')}</>
            )}
          </SubmitButton>
        </ActionRow>
      </Section>
    </PageContainer>
    </PageWrapper>
  )
}
